import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Button,
  DatePicker,
  EmptyState,
  TimetableIcon,
  TimetableView,
  type TimetableViewEntryType,
} from '@bp/ui-components';
import classNames from 'classnames';
import dayjs, { type Dayjs } from 'dayjs';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useTimetable } from 'hooks/useTimetable';
import { useTranslation } from 'react-i18next';
import styles from './Timetable.module.scss';

export type TimetableDay = {
  date: Dayjs;
  dayOfWeek: string;
  entries: TimetableViewEntryType[];
};

type TimetableProps = {
  profileUuid?: string;
  showDaySelect?: boolean;
  showLegend?: boolean;
  showHeader?: boolean;
  hideTodayHighlight?: boolean;
  days?: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  pagingInterval?: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  context: 'classbook' | 'dashboard' | 'homework';
  eventColorSource?: 'class' | 'subject';
  selectedDay: Dayjs;
  selectedEntryUuid?: string;
  noFuture?: boolean;
  setSelectedDay: (date: Dayjs) => void;
  onDateChanged?: (newDate: Date) => void;
  onEventClick?: ({
    eventUuid,
    eventTitle,
    subjectUuid,
    groupUuid,
  }: {
    eventUuid: string;
    eventTitle?: string | null;
    subjectUuid?: string | null;
    groupUuid?: string | null;
  }) => void;
};

export const Timetable = ({
  profileUuid,
  showDaySelect,
  showLegend,
  showHeader,
  hideTodayHighlight,
  days = 5,
  pagingInterval = 1,
  context,
  eventColorSource,
  selectedDay,
  selectedEntryUuid,
  noFuture,
  setSelectedDay,
  onDateChanged,
  onEventClick,
}: TimetableProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();

  const preloadDates: Date[] = [];

  for (let i = 0; i < 7; i++) {
    preloadDates.push(selectedDay.startOf('week').add(i, 'days').toDate());
  }

  const { getTimetableStartEnd, getTimetableDays } = useTimetable(
    profileUuid ?? pimAuthClaims.getProfile().uuid,
    preloadDates,
    context,
  );

  const { start, end } = getTimetableStartEnd();

  const timetableDays: TimetableDay[] = getTimetableDays(
    selectedDay,
    days,
    selectedEntryUuid,
    onEventClick,
    eventColorSource,
  );

  return (
    <div className={styles.timetable}>
      {showDaySelect && (
        <div className={styles.actions}>
          <div className={styles.date}>
            <Button
              name='decrease'
              hierarchy='tertiary'
              onClick={() => {
                const newDate = selectedDay.subtract(pagingInterval, 'days');
                setSelectedDay(newDate);
                onDateChanged && onDateChanged(newDate.toDate());
              }}
              icon={<ArrowLeftIcon />}
            />
            <DatePicker
              className={styles.picker}
              onChange={(e) => {
                const newDate = dayjs(e);
                setSelectedDay(newDate);
                onDateChanged && onDateChanged(newDate.toDate());
              }}
              name='date'
              value={selectedDay.toDate()}
              maxDate={noFuture ? new Date() : undefined}
              dense
            />
            <Button
              name='increase'
              hierarchy='tertiary'
              disabled={noFuture && selectedDay.isSameOrAfter(dayjs(), 'days')}
              onClick={() => {
                const newDate = selectedDay.add(pagingInterval, 'days');
                setSelectedDay(newDate);
                onDateChanged && onDateChanged(newDate.toDate());
              }}
              icon={<ArrowRightIcon />}
            />
          </div>
          <Button
            className={styles.today}
            hierarchy='secondary'
            onClick={() => {
              const newDate = dayjs();
              setSelectedDay(newDate);
              onDateChanged && onDateChanged(newDate.toDate());
            }}
            disabled={selectedDay.isSame(dayjs(), 'day')}
          >
            {t('common.today')}
          </Button>
        </div>
      )}

      <div className={styles['timetable-views']}>
        {timetableDays.length === 0 ? (
          <EmptyState title={t('timetable.noTimetableDays')} icon={<TimetableIcon />} forcedHeight='330px' />
        ) : (
          <>
            {showLegend && (
              <TimetableView
                className={classNames(styles.legend, {
                  [styles.spacing]: showHeader,
                })}
                start={start.toDate()}
                end={end.toDate()}
                entries={[]}
                showTimegrid
              />
            )}
            {timetableDays.map(({ date, dayOfWeek, entries }) => {
              return (
                <div
                  key={date.toISOString()}
                  className={classNames(styles.day, {
                    [styles.active]: !hideTodayHighlight && date.isSame(dayjs(), 'day'),
                  })}
                  style={{ flexBasis: days === 1 ? 'auto' : `${100 / 5}%` }}
                >
                  {showHeader && (
                    <div className={styles['day-header']}>
                      <div className={styles.name}>{dayOfWeek}</div>
                      <div className={styles.date}>{date.format('DD. MMMM YY')}</div>
                    </div>
                  )}
                  <TimetableView start={start.toDate()} end={end.toDate()} entries={entries} />
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};
